import api from "../core/http";

/**
 * Get Reading orders list by type
 * - uses react-query
 * @param {*} type
 * @returns
 */
export const getLatestReadingOrders = () => {
  return api
    .get(`/reading-orders/latest`)
    .then((res) => res.data)
    .catch((err) => err);
};

/**
 * Browse discovery lists (popular / trending up / latest published).
 * Each one returns at most 25 reading orders and is cached by the API.
 * - uses react-query
 */
export const getPopularReadingOrders = () => {
  return api.get("/v1/reading-orders/popular").then((res) => res.data);
};

export const getTrendingReadingOrders = () => {
  return api.get("/v1/reading-orders/trending").then((res) => res.data);
};

export const getLatestPublishedReadingOrders = () => {
  return api.get("/v1/reading-orders/latest").then((res) => res.data);
};

/**
 * All reading orders and readings related API calls
 */

/**
 * Get Reading orders list by type
 * - uses react-query
 * @param {*} type
 * @returns
 */
export const getReadingOrder = (type, franchise = 1, page = 1, paywallSort) => {
  return api
    .get(`/v1/reading-orders/${type}`, {
      params: {
        franchise,
        page,
        ...(paywallSort ? { paywall_sort: paywallSort } : {}),
      },
    })
    .then((res) => res.data);
};

/**
 * Get a single reading order
 * @param {*} readingOrderId
 * @returns
 */
export const getSingleReadingOrder = (readingOrderId) => {
  return api
    .get(`/v1/reading-orders/${readingOrderId}/show`)
    .then((res) => res.data)
    .catch((err) => err);
};

/**
 * Get Comics for a reading order
 * - uses react-query
 * @param {*} type
 * @returns
 */
export const getComics = (readingOrderId, page = 1) => {
  return api
    .get(`/v1/reading-orders/${readingOrderId}/comics?page=${page}`)
    .then((res) => res.data)
    .catch((err) => err);
};

/**
 * Get single Comic
 * - uses react-query
 * @param {*} type
 * @returns
 */
export const getComic = (comicId, story) => {
  const query = story ? `?story=${story}` : "";
  return api
    .get(`/v1/comics/${comicId}${query}`)
    .then((res) => res.data)
    .catch((err) => err);
};

/**
 * Start a new Reading order
 * @param {*} readingOrderId
 * @returns
 */
export const startReading = (readingOrderId) => {
  return api.post("/v1/readings", { readingOrderId });
};

/**
 * Get my Reading list
 * @returns
 */
export const getReadings = (franchise = 1, page = 1, completed = 0) => {
  return api
    .get("/v1/readings", { params: { franchise, completed, page } })
    .then((res) => res.data)
    .catch((err) => err);
};

/**
 * Get my reading of one specific reading order (any franchise), if any.
 * - uses react-query
 * @param {number} readingOrderId
 * @returns {Promise<object|null>} the reading or null
 */
export const getReadingForOrder = (readingOrderId) => {
  return api
    .get("/v1/readings", { params: { reading_order_id: readingOrderId } })
    .then(
      (res) => (Array.isArray(res.data?.data) ? res.data.data[0] : null) ?? null
    );
};

/**
 * Get specific reading info
 * @returns
 */
export const getReading = (readingId) => {
  return api
    .get(`/v1/readings/${readingId}`)
    .then((res) => res.data)
    .catch((err) => err);
};

/**
 * Delete a reading order
 * @returns
 */
export const deleteReading = (readingId) => {
  return api
    .delete(`/v1/readings/${readingId}`)
    .then((res) => res.data)
    .catch((err) => err);
};

/**
 * Mark as read OR skipped a reading
 * @returns
 */
export const markAsRead = (comicId, readingId, skipped, story) => {
  return api.post("/v1/readings/mark-as-read", {
    comicId,
    readingId,
    skipped,
    story,
  });
};

/**
 * Mark all previous comics as read (or skipped)
 * @returns
 */
export const markPreviousAsRead = (
  order,
  readingId,
  read = false,
  skipped = false,
  readLater = false
) => {
  return api.post("/v1/readings/exp/mark-previous-as-read", {
    order,
    readingId,
    read,
    skipped,
    readLater,
  });
};

/**
 * Undo skipped or read status
 * @returns
 */
export const markAsUnread = (comicId, readingId, skipped, story) => {
  return api.post("/v1/readings/mark-as-unread", {
    comicId,
    readingId,
    skipped,
    story,
  });
};

/**
 * Update reading
 * @returns
 */
export const updateReading = (readingId, reading) => {
  return api.put(`/v1/readings/${readingId}/update`, reading);
};

/**
 * Reset all reading order for that reading
 * @returns
 */
export const resetReading = (readingId) => {
  return api.post(`/v1/readings/${readingId}/reset`);
};

/**
 * Report a broken link for a comic
 * @returns
 */
export const reportBrokenLink = (comicId) => {
  return api
    .post("/v1/readings/report-broken-link", { comicId })
    .then((res) => res.data);
};

/**
 * Replace the stored reading link for a comic (admin only)
 * @returns
 */
export const comicFixLink = (comicId, url) => {
  return api.post(`/v1/comics/${comicId}/link`, { url });
};

/**
 * Get all comics and reading statuses
 * - uses react-query
 * @param {*} type
 * @returns
 */
export const getAllComics = (readingId, page = 1) => {
  return api
    .get(`/v1/readings/${readingId}/all-the-comics`, { params: { page } })
    .then((res) => res.data)
    .catch((err) => err);
};

/**
 * Get all comics and reading statuses
 * - uses react-query
 * @param {*} type
 * @returns
 */
export const getPreviousAllComics = (readingId, from = 0) => {
  return api
    .get(`/v1/readings/${readingId}/previous-all-comics`, { params: { from } })
    .then((res) => res.data)
    .catch((err) => err);
};

/**
 * Get My Reading orders list by franchise
 * - uses react-query
 * @param {*} franchise
 * @returns
 */
export const getMyReadingOrders = (
  franchise,
  type,
  sortBy,
  page = 1,
  paywallSort,
  searchTerm,
  published
) => {
  console.log("Call endpoint with ", franchise, type, sortBy);
  return api
    .get(`/v1/my-reading-orders/all`, {
      params: {
        franchise,
        type,
        sortBy,
        page,
        ...(paywallSort ? { paywall_sort: paywallSort } : {}),
        ...(searchTerm ? { searchTerm } : {}),
        ...(published !== undefined ? { published } : {}),
      },
    })
    .then((res) => res.data)
    .catch((err) => err);
};

/**
 * Get MY single reading order
 * @param {*} readingOrderId
 * @returns
 */
export const getMySingleReadingOrder = (readingOrderId) => {
  return api
    .get(`/v1/my-reading-orders/${readingOrderId}/show`)
    .then((res) => res.data)
    .catch((err) => err);
};

/**
 * Toggle publish share/private reading order
 * @param {*} readingOrderId
 * @returns
 */
export const publishMyReadingOrder = (readingOrderId, privacyMode) => {
  return api
    .post(`/v1/my-reading-orders/${readingOrderId}/set-visibility`, {
      published: 1,
      privacyMode,
    })
    .then((res) => res.data)
    .catch((err) => err);
};

/**
 * Get comics order for a single reading order
 * - uses react-query
 * @param {*} type
 * @returns
 */
export const getMyReadingOrderComics = (readingOrderId, page = 1) => {
  return api
    .get(`/v1/my-reading-orders/${readingOrderId}/comics`, {
      params: { page },
    })
    .then((res) => res.data)
    .catch((err) => err);
};

/**
 * Add a comic to MY reading order
 * @param {*} readingOrderId
 * @returns
 */
export const addComicInReadingOrder = (readingOrderId, comicId, order) => {
  return api
    .post(`/v1/my-reading-orders/${readingOrderId}/add-comic`, {
      comicId,
      order,
    })
    .then((res) => res.data)
    .catch((err) => err);
};

/**
 * Update a comic to MY reading order
 * @param {*} readingOrderId
 * @returns
 */
export const updateComicInReadingOrder = (
  readingOrderId,
  comicId,
  order,
  story = null
) => {
  console.log("updating...", comicId, order, story);
  return api
    .post(`/v1/my-reading-orders/${readingOrderId}/update-comic`, {
      comicId,
      order,
      ...(story !== null ? { story } : {}),
    })
    .then((res) => res.data)
    .catch((err) => err);
};

/**
 * Remove a comic to MY reading order
 * @param {*} readingOrderId
 * @returns
 */
export const removeComicInReadingOrder = (readingOrderId, comicId, order) => {
  return api
    .post(`/v1/my-reading-orders/${readingOrderId}/remove-comic`, {
      comicId,
      order,
    })
    .then((res) => res.data)
    .catch((err) => err);
};

/**
 * Finalise update on my reading order
 * @param {*} readingOrderId
 * @returns
 */
export const updateMyOrder = (readingOrderId) => {
  return api
    .post(`/v1/my-reading-orders/${readingOrderId}/finalise`)
    .then((res) => res.data)
    .catch((err) => err);
};

/**
 * Delete my reading order
 * @param {*} readingOrderId
 * @returns
 */
export const deleteMyOrder = (readingOrderId) => {
  return api
    .post(`/v1/my-reading-orders/${readingOrderId}/destroy`)
    .then((res) => res.data)
    .catch((err) => err);
};

/**
 * Submit a new import comic from a source
 * - uses react-query
 * @param {*} type
 * @returns
 */
export const submitComic = (source, url) => {
  return api
    .post(`/v1/my-reading-orders/submit-comic`, { source, url })
    .then((res) => res.data)
    .catch((err) => err);
};

/**
 * Clone a reading order and add it to My Orders
 * - uses react-query
 * @param {*} type
 * @returns
 */
export const cloneReadingOrder = (readingOrderId) => {
  return api
    .post(`/v1/reading-orders/${readingOrderId}/clone`)
    .then((res) => res.data)
    .catch((err) => err);
};

/**
 * Get the comic row of a reading order
 * - uses react-query
 * @param {*} readingOrderId
 * @param {*} order
 * @returns
 */
export const getComicRow = (readingOrderId, order) => {
  return api
    .get(`/v1/reading-orders/${readingOrderId}/comics/row`, {
      params: { order },
    })
    .then((res) => res.data)
    .catch((err) => err);
};

/**
 * Submit a new correction to add comics to a reading order
 * - uses react-query
 * @param {*} type
 * @returns
 */
export const submitAddCorrection = (readingOrderId, order, row, comics) => {
  return api
    .post(`/v1/reading-orders/${readingOrderId}/corrections/add`, {
      order,
      row,
      comics,
    })
    .then((res) => res.data)
    .catch((err) => err);
};

/**
 * Submit a new correction to Edit a comic in a reading order
 * - uses react-query
 * @param {*} type
 * @returns
 */
export const submitEditCorrection = (readingOrderId, row, comicId, story) => {
  return api
    .post(`/v1/reading-orders/${readingOrderId}/corrections/edit`, {
      row,
      comicId,
      story,
    })
    .then((res) => res.data)
    .catch((err) => err);
};

/**
 * Submit a new correction to Edit a comic in a reading order
 * - uses react-query
 * @param {*} type
 * @returns
 */
export const submitDeleteCorrection = (readingOrderId, row) => {
  return api
    .post(`/v1/reading-orders/${readingOrderId}/corrections/delete`, {
      row,
    })
    .then((res) => res.data)
    .catch((err) => err);
};

/**
 * Get the list of corrections made by user on that reading Order
 * - uses react-query
 * @param {*} type
 * @returns
 */
export const getCorrections = (readingOrderId) => {
  return api
    .get(`/v1/reading-orders/${readingOrderId}/corrections`)
    .then((res) => res.data)
    .catch((err) => err);
};

/**
 * Let the user undo their own correction
 * - uses react-query
 * @param {*} type
 * @returns
 */
export const undoCorrection = (readingOrderId, correctionId) => {
  return api
    .post(`/v1/reading-orders/${readingOrderId}/corrections/${correctionId}`)
    .then((res) => res.data)
    .catch((err) => err);
};

/**
 * Get Last reading the user
 * - uses react-query
 * @param {*} type
 * @returns
 */
export const getLastReading = () => {
  return api
    .get("/v1/readings/last-read")
    .then((res) => res.data)
    .catch((err) => err);
};
